import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useQuery } from 'react-query';
import {
  Scale, ChevronDown, ChevronRight, User, Calendar, Briefcase,
  FileText, Search, Quote,
} from 'lucide-react';
import type { ExpertEvidenceSummary, ExpertOpinion } from '../../hooks/useMetadataExtraction';
import { useDocumentViewer } from '../../contexts/DocumentViewerContext';
import { DerivedOutputBanner } from '../review/DerivedOutputBanner';
import { EntryActions } from '../ask/EntryActions';
import type { ContextItem } from '../../contexts/AskSidebarContext';
import { useKeyFindings } from '../../contexts/KeyFindingsContext';
import { useHighlightParam } from '../../hooks/useHighlightParam';

interface MetadataExtractionResponse {
  expertEvidence?: ExpertEvidenceSummary[];
  [key: string]: any;
}

interface ExpertEvidenceTabProps {
  caseId: string;
}

const TOPIC_LABELS: Record<string, string> = {
  CAUSATION: 'Causation',
  PROGNOSIS: 'Prognosis',
  CURRENT_CONDITION: 'Current Condition',
  LIABILITY: 'Liability',
  TREATMENT: 'Treatment',
  CAPACITY: 'Capacity',
  QUANTUM: 'Quantum',
  OTHER: 'Other',
};

const TOPIC_COLORS: Record<string, string> = {
  CAUSATION: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  PROGNOSIS: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
  CURRENT_CONDITION: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300',
  LIABILITY: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  TREATMENT: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
  CAPACITY: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  QUANTUM: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  OTHER: 'bg-gray-100 text-gray-700 dark:bg-gray-800/30 dark:text-gray-300',
};

// --- Key Insights ---

interface ExpertInsights {
  opinionCount: number;
  topicBreakdown: Record<string, number>;
  summaryLine: string;
}

function extractInsights(evidence: ExpertEvidenceSummary[]): ExpertInsights {
  let opinionCount = 0;
  const topicBreakdown: Record<string, number> = {};

  for (const report of evidence) {
    for (const op of report.opinions || []) {
      opinionCount++;
      const topic = op.topic || 'OTHER';
      topicBreakdown[topic] = (topicBreakdown[topic] || 0) + 1;
    }
  }

  const topTopics = Object.entries(topicBreakdown)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([t]) => TOPIC_LABELS[t] || t);

  const summaryLine = opinionCount > 0
    ? `${opinionCount} opinion${opinionCount !== 1 ? 's' : ''} extracted from ${evidence.length} expert report${evidence.length !== 1 ? 's' : ''}${topTopics.length > 0 ? `, covering ${topTopics.join(', ')}` : ''}.`
    : `${evidence.length} expert report${evidence.length !== 1 ? 's' : ''} identified. No structured opinions extracted.`;

  return { opinionCount, topicBreakdown, summaryLine };
}

// --- Main Component ---

export const ExpertEvidenceTab: React.FC<ExpertEvidenceTabProps> = ({ caseId }) => {
  const { openDocument } = useDocumentViewer();

  const { data: metadata } = useQuery<MetadataExtractionResponse | null>(
    ['metadata', caseId],
    { enabled: false }
  );

  const expertEvidence = metadata?.expertEvidence || [];
  const insights = useMemo(() => extractInsights(expertEvidence), [expertEvidence]);

  // Track which expert cards are expanded to show opinions
  const [expandedExperts, setExpandedExperts] = useState<Set<string>>(new Set());
  // Track which individual opinions are expanded for detail
  const [expandedOpinions, setExpandedOpinions] = useState<Set<string>>(new Set());

  const toggleExpert = useCallback((id: string) => {
    setExpandedExperts(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }, []);
  const toggleOpinion = useCallback((id: string) => {
    setExpandedOpinions(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }, []);

  // Auto-expand and scroll to a highlighted opinion from URL param
  const { highlightId, scrollToHighlighted } = useHighlightParam();
  useEffect(() => {
    if (!highlightId || !highlightId.includes('-op-')) return;
    // Extract expert ID (documentId) from {docUUID}-op-{idx}
    const expertId = highlightId.replace(/-op-\d+$/, '');
    setExpandedExperts(prev => new Set(prev).add(expertId));
    setExpandedOpinions(prev => new Set(prev).add(highlightId));
    requestAnimationFrame(() => scrollToHighlighted());
  }, [highlightId, scrollToHighlighted]);

  const handleOpenCitation = useCallback((documentId: string, pageNumber: number) => {
    openDocument({ documentId, documentName: 'Expert Report', pageNumber });
  }, [openDocument]);

  // Group by speciality
  const grouped = useMemo(() => {
    const map = new Map<string, ExpertEvidenceSummary[]>();
    for (const ev of expertEvidence) {
      const key = ev.speciality || 'Unspecified';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(ev);
    }
    return map;
  }, [expertEvidence]);

  if (expertEvidence.length === 0) {
    return (
      <div className="p-6 h-full overflow-auto space-y-6">
        <div className="mb-2">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-medical-100 mb-1">Expert Evidence</h3>
          <p className="text-sm text-gray-600 dark:text-medical-400">
            Structured summaries of expert witness opinions, organised by speciality and topic.
          </p>
        </div>
        <PendingState />
      </div>
    );
  }

  return (
    <div className="p-6 h-full overflow-auto space-y-6">
      {/* Header */}
      <div className="mb-2">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-medical-100 mb-1">Expert Evidence</h3>
        <p className="text-sm text-gray-600 dark:text-medical-400">
          Structured summaries of expert witness opinions, organised by speciality and topic.
        </p>
      </div>

      <DerivedOutputBanner
        caseId={caseId}
        analysisLabel="expert evidence summary"
        evaluatePrompt="I have corrected some chronology events since the expert evidence summaries were generated. Review the edited events and assess whether the corrections affect any expert opinions, conclusions, or recommendations referenced in this tab."
      />

      {/* Key Insights */}
      <div className="bg-gray-50 dark:bg-medical-800/50 border border-gray-200 dark:border-medical-700 rounded-lg p-4 space-y-4">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase text-gray-500 dark:text-medical-400 tracking-wide">
          <Search className="w-3.5 h-3.5" />
          Key Insights
        </div>

        <p className="text-sm text-gray-700 dark:text-medical-300">{insights.summaryLine}</p>

        {/* Topic breakdown pills */}
        {Object.keys(insights.topicBreakdown).length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(insights.topicBreakdown)
              .sort((a, b) => b[1] - a[1])
              .map(([topic, count]) => (
                <span
                  key={topic}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${TOPIC_COLORS[topic] || TOPIC_COLORS.OTHER}`}
                >
                  {TOPIC_LABELS[topic] || topic} ({count})
                </span>
              ))}
          </div>
        )}
      </div>

      {/* Expert opinion disclaimer */}
      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-4 py-3">
        <p className="text-sm text-amber-800 dark:text-amber-200">
          <strong>Expert opinion</strong> — The content below represents expert witness opinions and analysis,
          not primary clinical records. Expert opinions are presented separately from the chronology for clarity.
        </p>
      </div>

      {/* Expert reports grouped by speciality */}
      {Array.from(grouped.entries()).map(([speciality, reports]) => (
        <div key={speciality} className="space-y-4">
          <h3 className="text-sm font-semibold text-medical-600 dark:text-medical-400 uppercase tracking-wider border-b border-medical-200 dark:border-medical-700 pb-1">
            {speciality}
          </h3>
          {reports.map((report) => {
            const expertId = report.documentId;
            const isExpanded = expandedExperts.has(expertId);
            const opinionCount = report.opinions?.length || 0;

            return (
              <div
                key={expertId}
                className="border border-amber-200 dark:border-amber-800 rounded-lg overflow-hidden"
              >
                {/* Expert header — always visible */}
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 px-5 py-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-base font-semibold text-medical-900 dark:text-medical-100 flex items-center gap-2">
                        <User className="h-4 w-4 text-amber-600" />
                        {report.expertName || 'Unknown Expert'}
                      </h3>
                      {report.qualifications && (
                        <p className="text-sm text-medical-600 dark:text-medical-400 mt-0.5">{report.qualifications}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-4 mt-3 text-sm text-medical-600 dark:text-medical-400">
                    {report.instructedBy && (
                      <span className="flex items-center gap-1.5">
                        <Briefcase className="h-3.5 w-3.5" />
                        {report.instructedBy}
                      </span>
                    )}
                    {report.examinationDate && (
                      <span className="flex items-center gap-1.5">
                        <Calendar className="h-3.5 w-3.5" />
                        Examined: {new Date(report.examinationDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                      </span>
                    )}
                    {report.reportDate && (
                      <span className="flex items-center gap-1.5">
                        <FileText className="h-3.5 w-3.5" />
                        Report: {new Date(report.reportDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                      </span>
                    )}
                  </div>
                </div>

                {/* Conclusions summary — always visible, never truncated */}
                {report.conclusions && (
                  <div className={`px-5 py-3 ${!isExpanded && opinionCount > 0 ? 'border-b border-amber-100 dark:border-amber-900/30' : ''}`}>
                    <p className="text-sm text-gray-700 dark:text-medical-300 leading-relaxed">{report.conclusions}</p>
                  </div>
                )}

                {/* Collapsed: "Show X opinions" link */}
                {!isExpanded && opinionCount > 0 && (
                  <button
                    onClick={() => toggleExpert(expertId)}
                    className="w-full px-5 py-2.5 text-left hover:bg-amber-50/50 dark:hover:bg-amber-900/10 transition-colors flex items-center gap-2"
                  >
                    <ChevronRight className="w-4 h-4 flex-shrink-0 text-gray-400 dark:text-medical-500" />
                    <span className="text-sm text-cyan-600 dark:text-cyan-400 font-medium">
                      Show {opinionCount} {opinionCount === 1 ? 'opinion' : 'opinions'}
                    </span>
                  </button>
                )}

                {/* Expanded: opinion list */}
                {isExpanded && opinionCount > 0 && (
                  <>
                    {/* Section header with collapse */}
                    <button
                      onClick={() => toggleExpert(expertId)}
                      className="w-full flex items-center justify-between px-5 py-2.5 bg-amber-50/50 dark:bg-amber-900/10 border-y border-amber-100 dark:border-amber-800/50"
                    >
                      <div className="flex items-center gap-2">
                        <Scale className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-medical-400">
                          Key Opinions ({opinionCount})
                        </h4>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-gray-500 dark:text-medical-400">{opinionCount}</span>
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      </div>
                    </button>

                    {/* Opinion cards */}
                    <div className="space-y-0.5">
                      {(report.opinions || []).map((opinion, idx) => (
                        <OpinionRow
                          key={`${expertId}-op-${idx}`}
                          opId={`${expertId}-op-${idx}`}
                          opinion={opinion}
                          report={report}
                          isExpanded={expandedOpinions.has(`${expertId}-op-${idx}`)}
                          onToggle={() => toggleOpinion(`${expertId}-op-${idx}`)}
                          onOpenCitation={handleOpenCitation}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

// --- Opinion row ---

const OpinionRow: React.FC<{
  opId: string;
  opinion: ExpertOpinion;
  report: ExpertEvidenceSummary;
  isExpanded: boolean;
  onToggle: () => void;
  onOpenCitation: (documentId: string, pageNumber: number) => void;
}> = ({ opId, opinion, report, isExpanded, onToggle, onOpenCitation }) => {
  const topic = opinion.topic || 'OTHER';
  const citations = opinion.citations || [];
  const { isEventMarked } = useKeyFindings();
  const isMarked = isEventMarked(opId);

  const contextItem: ContextItem = {
    id: opId,
    type: 'expert-opinion',
    summary: (opinion.opinion || '').slice(0, 80),
    source: `Expert Evidence — ${report.expertName || 'Unknown'}`,
    data: {
      expertName: report.expertName,
      speciality: report.speciality,
      topic: opinion.topic,
      opinion: opinion.opinion,
      reasoning: opinion.reasoning,
      documentId: report.documentId,
    },
  };

  return (
    <div data-event-id={contextItem.id} className={`rounded-lg border transition-all duration-150 ${
      isMarked
        ? 'border-amber-300 dark:border-amber-600 shadow-sm'
        : 'border-transparent'
    } ${isExpanded ? 'bg-gray-50/50 dark:bg-medical-700/20' : ''}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-start gap-2 p-3 px-5 text-left hover:bg-gray-50/70 dark:hover:bg-medical-700/30 transition-colors group rounded-lg"
      >
        {isExpanded
          ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
          : <ChevronRight className="w-4 h-4 text-gray-300 dark:text-medical-600 group-hover:text-gray-500 dark:group-hover:text-medical-400 flex-shrink-0 mt-0.5 transition-colors" />}
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold flex-shrink-0 mt-0.5 ${TOPIC_COLORS[topic] || TOPIC_COLORS.OTHER}`}>
          {TOPIC_LABELS[topic] || topic}
        </span>
        <span className={`text-sm text-gray-900 dark:text-medical-100 flex-1 ${isExpanded ? 'font-medium' : ''}`}>
          {opinion.opinion}
        </span>
        <EntryActions
          compact
          item={contextItem}
        />
      </button>

      {isExpanded && (
        <div className="px-5 pb-3 pt-0 ml-6 space-y-3 text-sm text-gray-700 dark:text-medical-300">
          <div>
            <span className="text-xs font-semibold text-medical-500 dark:text-medical-400 uppercase tracking-wider">Opinion</span>
            <p className="mt-1">{opinion.opinion}</p>
          </div>
          {opinion.reasoning && (
            <div>
              <span className="text-xs font-semibold text-medical-500 dark:text-medical-400 uppercase tracking-wider">Reasoning</span>
              <p className="mt-1">{opinion.reasoning}</p>
            </div>
          )}
          {citations.length > 0 && (
            <div>
              <span className="text-xs font-semibold text-medical-500 dark:text-medical-400 uppercase tracking-wider">
                Source{citations.length !== 1 ? 's' : ''}
              </span>
              <div className="mt-1.5 space-y-1.5">
                {citations.map((cit: any, i: number) => (
                  <CitationLink
                    key={i}
                    citation={cit}
                    documentName={report.documentName}
                    onOpen={onOpenCitation}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// --- Citation link component ---

const CitationLink: React.FC<{
  citation: { documentId?: string; pageNumber?: number; text?: string };
  documentName?: string;
  onOpen: (documentId: string, pageNumber: number) => void;
}> = ({ citation, documentName, onOpen }) => {
  if (!citation.documentId) return null;
  return (
    <button
      onClick={() => onOpen(citation.documentId!, citation.pageNumber || 1)}
      className="flex items-start gap-2 px-3 py-2 rounded-md bg-medical-50 dark:bg-medical-800/50 border border-medical-200 dark:border-medical-700 hover:border-amber-300 dark:hover:border-amber-700 transition-colors text-left w-full group"
    >
      <Quote className="w-3.5 h-3.5 text-amber-500 dark:text-amber-400 flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        {citation.text && (
          <p className="text-xs text-medical-700 dark:text-medical-300 italic line-clamp-2">"{citation.text}"</p>
        )}
        <p className="text-[10px] text-medical-500 dark:text-medical-400 mt-0.5 group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors">
          {documentName || 'Source document'}{citation.pageNumber ? `, p.${citation.pageNumber}` : ''}
        </p>
      </div>
    </button>
  );
};

// --- Shared sub-components ---

function PendingState() {
  return (
    <div className="text-center py-12 max-w-lg mx-auto">
      <Scale className="w-10 h-10 text-gray-300 dark:text-medical-600 mx-auto mb-3" />
      <p className="text-sm font-medium text-gray-700 dark:text-medical-300 mb-2">
        No expert witness reports identified
      </p>
      <p className="text-sm text-gray-500 dark:text-medical-400 mb-4">
        Expert witness reports are medico-legal reports prepared by instructed specialists — such as
        consultants, psychologists, or occupational therapists — who examine the claimant and provide
        professional opinions on causation, prognosis, and condition.
      </p>
      <p className="text-xs text-gray-400 dark:text-medical-500">
        To see expert opinions here, upload expert witness reports via the <strong>Documents</strong> tab,
        then re-run the analysis.
      </p>
    </div>
  );
}
